import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const tabs = [
  { key: "sessions", label: "Exam Sessions", icon: "bi-calendar2-check" },
  { key: "assessments", label: "Assessments", icon: "bi-journal-text" },
  { key: "marks", label: "Marks Entry", icon: "bi-pencil-square" },
  { key: "reports", label: "Report Cards", icon: "bi-file-earmark-medical" },
];

const emptySession = {
  id: null,
  academic_year: "",
  term: "",
  name: "",
  start_date: "",
  end_date: "",
  status: "DRAFT",
  publish_to_students: false,
  restrict_unpaid_students: true,
  report_footer: "",
};

const emptyAssessment = {
  id: null,
  session: "",
  class_group: "",
  class_subject: "",
  assessment_type: "",
  teacher: "",
  max_score: "100",
  weight: "100",
  due_date: "",
  status: "DRAFT",
  notes: "",
};

function ExamsResults() {
  const [activeTab, setActiveTab] = useState("sessions");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [sessions, setSessions] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [marks, setMarks] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teacherAllocations, setTeacherAllocations] = useState([]);
  const [students, setStudents] = useState([]);
  const [sessionForm, setSessionForm] = useState(emptySession);
  const [assessmentForm, setAssessmentForm] = useState(emptyAssessment);
  const [selectedAssessment, setSelectedAssessment] = useState("");
  const [markRows, setMarkRows] = useState([]);
  const [reportFilters, setReportFilters] = useState({ session: "", class_group: "", student: "" });
  const [reportCard, setReportCard] = useState(null);

  const activeSession = useMemo(
    () => sessions.find((item) => String(item.id) === String(selectedAssessmentSession(assessments, selectedAssessment))),
    [sessions, assessments, selectedAssessment]
  );

  const filteredClassSubjects = useMemo(() => {
    if (!assessmentForm.class_group) return classSubjects;
    return classSubjects.filter((item) => String(item.class_group) === String(assessmentForm.class_group));
  }, [classSubjects, assessmentForm.class_group]);

  const selectedAssessmentRecord = useMemo(
    () => assessments.find((item) => String(item.id) === String(selectedAssessment)),
    [assessments, selectedAssessment]
  );

  const filteredAssessmentTeachers = useMemo(() => {
    if (!assessmentForm.class_subject) return teachers;

    const selectedClassSubject = classSubjects.find(
      (item) => String(item.id) === String(assessmentForm.class_subject)
    );
    const selectedClassGroup = classGroups.find(
      (item) => String(item.id) === String(selectedClassSubject?.class_group || assessmentForm.class_group)
    );

    const allowedTeacherIds = new Set(
      teacherAllocations
        .filter(
          (allocation) =>
            String(allocation.class_subject) === String(assessmentForm.class_subject) &&
            allocation.is_active !== false
        )
        .map((allocation) => String(allocation.teacher))
    );

    if (
      selectedClassSubject?.subject_type === "CLASS_TEACHER" &&
      selectedClassGroup?.class_teacher
    ) {
      allowedTeacherIds.add(String(selectedClassGroup.class_teacher));
    }

    const allowedTeachers = teachers.filter((teacher) => allowedTeacherIds.has(String(teacher.id)));

    return allowedTeachers.length > 0 ? allowedTeachers : teachers;
  }, [assessmentForm.class_subject, assessmentForm.class_group, classSubjects, classGroups, teacherAllocations, teachers]);


  const loadAll = async () => {
    try {
      setLoading(true);
      const [
        summaryResponse,
        sessionsResponse,
        assessmentsResponse,
        marksResponse,
        yearsResponse,
        termsResponse,
        groupsResponse,
        subjectsResponse,
        assessmentTypesResponse,
        teachersResponse,
        teacherAllocationsResponse,
      ] = await Promise.all([
        axiosInstance.get("exams/summary/"),
        axiosInstance.get("exams/sessions/"),
        axiosInstance.get("exams/assessments/"),
        axiosInstance.get("exams/marks/"),
        axiosInstance.get("academic/years/"),
        axiosInstance.get("academic/terms/"),
        axiosInstance.get("academic/class-groups/"),
        axiosInstance.get("exams/class-subjects/"),
        axiosInstance.get("settings/assessment-types/"),
        axiosInstance.get("academic/setup/teachers/"),
        axiosInstance.get("academic/teacher-allocations/"),
      ]);

      setSummary(summaryResponse.data || {});
      setSessions(sessionsResponse.data || []);
      setAssessments(assessmentsResponse.data || []);
      setMarks(marksResponse.data || []);
      setAcademicYears(yearsResponse.data || []);
      setTerms(termsResponse.data || []);
      setClassGroups(groupsResponse.data || []);
      setClassSubjects(subjectsResponse.data || []);
      setAssessmentTypes(assessmentTypesResponse.data || []);
      setTeachers(teachersResponse.data || []);
      setTeacherAllocations(teacherAllocationsResponse.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load exams and results.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const saveSession = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = cleanPayload(sessionForm);
      if (sessionForm.id) {
        await axiosInstance.patch(`exams/sessions/${sessionForm.id}/`, payload);
        toast.success("Exam session updated.");
      } else {
        await axiosInstance.post("exams/sessions/", payload);
        toast.success("Exam session added.");
      }
      setSessionForm(emptySession);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save exam session.");
    } finally {
      setLoading(false);
    }
  };

  const editSession = (session) => {
    setSessionForm({
      id: session.id,
      academic_year: session.academic_year || "",
      term: session.term || "",
      name: session.name || "",
      start_date: session.start_date || "",
      end_date: session.end_date || "",
      status: session.status || "DRAFT",
      publish_to_students: Boolean(session.publish_to_students),
      restrict_unpaid_students: Boolean(session.restrict_unpaid_students),
      report_footer: session.report_footer || "",
    });
  };

  const editAssessment = (assessment) => {
    setAssessmentForm({
      id: assessment.id,
      session: assessment.session || "",
      class_group: assessment.class_group || "",
      class_subject: assessment.class_subject || "",
      assessment_type: assessment.assessment_type || "",
      teacher: assessment.teacher || "",
      max_score: assessment.max_score || "100",
      weight: assessment.weight || "100",
      due_date: assessment.due_date || "",
      status: assessment.status || "DRAFT",
      notes: assessment.notes || "",
    });
  };

  const saveAssessment = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = cleanPayload(assessmentForm);
      if (assessmentForm.id) {
        await axiosInstance.patch(`exams/assessments/${assessmentForm.id}/`, payload);
        toast.success("Assessment updated.");
      } else {
        await axiosInstance.post("exams/assessments/", payload);
        toast.success("Assessment added.");
      }
      setAssessmentForm(emptyAssessment);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save assessment.");
    } finally {
      setLoading(false);
    }
  };

  const deleteRecord = async (url, label) => {
    if (!window.confirm(`Delete ${label}?`)) return;
    try {
      setLoading(true);
      await axiosInstance.delete(url);
      toast.success(`${label} deleted.`);
      await loadAll();
    } catch (error) {
      toast.error(`Could not delete ${label}. It may already be in use.`);
    } finally {
      setLoading(false);
    }
  };

  const loadMarksForAssessment = async (assessmentId) => {
    setSelectedAssessment(assessmentId);
    setMarkRows([]);
    if (!assessmentId) return;

    const assessment = assessments.find((item) => String(item.id) === String(assessmentId));
    if (!assessment) return;

    try {
      setLoading(true);
      const [studentsResponse, marksResponse] = await Promise.all([
        axiosInstance.get(`exams/students/?class_group=${assessment.class_group}`),
        axiosInstance.get(`exams/marks/?assessment=${assessmentId}`),
      ]);
      const loadedStudents = studentsResponse.data || [];
      const loadedMarks = marksResponse.data || [];
      setStudents(loadedStudents);
      setMarks((prev) => {
        const otherMarks = prev.filter((item) => String(item.assessment) !== String(assessmentId));
        return [...otherMarks, ...loadedMarks];
      });
      setMarkRows(
        loadedStudents.map((student) => {
          const existing = loadedMarks.find((mark) => String(mark.student) === String(student.id));
          return {
            student: student.id,
            student_name: student.full_name,
            student_number: student.student_number,
            raw_score: existing?.raw_score ?? "",
            is_absent: Boolean(existing?.is_absent),
            remark: existing?.remark || "",
            grade: existing?.grade || "",
            percentage: existing?.percentage || "",
            status: existing?.status || "SUBMITTED",
          };
        })
      );
    } catch (error) {
      toast.error("Failed to load assessment marks.");
    } finally {
      setLoading(false);
    }
  };

  const updateMarkRow = (studentId, field, value) => {
    setMarkRows((prev) =>
      prev.map((row) => (String(row.student) === String(studentId) ? { ...row, [field]: value } : row))
    );
  };

  const saveMarks = async (event) => {
    event.preventDefault();
    if (!selectedAssessment) {
      toast.warning("Choose an assessment first.");
      return;
    }
    try {
      setLoading(true);
      await axiosInstance.post("exams/marks/bulk/", {
        assessment: selectedAssessment,
        marks: markRows.map((row) => ({
          student: row.student,
          raw_score: row.is_absent ? null : row.raw_score,
          is_absent: row.is_absent,
          remark: row.remark,
          status: row.status || "SUBMITTED",
        })),
      });
      toast.success("Marks saved.");
      await loadMarksForAssessment(selectedAssessment);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save marks.");
    } finally {
      setLoading(false);
    }
  };

  const loadReportStudents = async (classGroupId) => {
    setReportFilters((prev) => ({ ...prev, class_group: classGroupId, student: "" }));
    if (!classGroupId) {
      setStudents([]);
      return;
    }
    try {
      const response = await axiosInstance.get(`exams/students/?class_group=${classGroupId}`);
      setStudents(response.data || []);
    } catch (error) {
      toast.error("Failed to load students.");
    }
  };

  const openReportCard = async (event) => {
    event.preventDefault();
    if (!reportFilters.session || !reportFilters.student) {
      toast.warning("Choose a session and student.");
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `exams/report-card/?session=${reportFilters.session}&student=${reportFilters.student}`
      );
      setReportCard(response.data);
    } catch (error) {
      showError(error, "Failed to load report card.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h5 className="fw-bold mb-1">Exams & Results</h5>
              <p className="text-muted mb-0">
                Manage exam sessions, assessments, marks, report cards, and fee-aware result access.
              </p>
            </div>
            <button className="btn btn-outline-success" onClick={loadAll} disabled={loading}>
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>

          <div className="row g-2 mt-3">
            <Summary label="Sessions" value={summary.sessions || 0} />
            <Summary label="Open Sessions" value={summary.open_sessions || 0} />
            <Summary label="Assessments" value={summary.assessments || 0} />
            <Summary label="Marks Recorded" value={summary.marks || 0} />
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`btn btn-sm ${activeTab === tab.key ? "btn-success" : "btn-light border"}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`bi ${tab.icon} me-2`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "sessions" && (
        <SetupCard title="Exam Sessions">
          <form className="row g-3 mb-4" onSubmit={saveSession}>
            <SelectField label="Academic Year" value={sessionForm.academic_year} onChange={(value) => setSessionForm((prev) => ({ ...prev, academic_year: value }))} options={academicYears.map((item) => [item.id, item.name])} />
            <SelectField label="Term" value={sessionForm.term} onChange={(value) => setSessionForm((prev) => ({ ...prev, term: value }))} options={terms.map((item) => [item.id, item.academic_year_name ? `${item.academic_year_name} - ${item.name}` : item.name])} required={false} />
            <TextField label="Session Name" value={sessionForm.name} onChange={(value) => setSessionForm((prev) => ({ ...prev, name: value }))} placeholder="Term 1 Final Exams" required />
            <TextField label="Start Date" type="date" value={sessionForm.start_date || ""} onChange={(value) => setSessionForm((prev) => ({ ...prev, start_date: value }))} />
            <TextField label="End Date" type="date" value={sessionForm.end_date || ""} onChange={(value) => setSessionForm((prev) => ({ ...prev, end_date: value }))} />
            <SelectField label="Status" value={sessionForm.status} onChange={(value) => setSessionForm((prev) => ({ ...prev, status: value }))} options={[["DRAFT", "Draft"], ["OPEN", "Open"], ["CLOSED", "Closed"], ["PUBLISHED", "Published"]]} />
            <SwitchField label="Publish to students" checked={sessionForm.publish_to_students} onChange={(value) => setSessionForm((prev) => ({ ...prev, publish_to_students: value }))} />
            <SwitchField label="Restrict unpaid students" checked={sessionForm.restrict_unpaid_students} onChange={(value) => setSessionForm((prev) => ({ ...prev, restrict_unpaid_students: value }))} />
            <div className="col-12">
              <label className="form-label fw-semibold">Report Footer</label>
              <textarea className="form-control" rows="2" value={sessionForm.report_footer || ""} onChange={(event) => setSessionForm((prev) => ({ ...prev, report_footer: event.target.value }))} />
            </div>
            <SaveBar loading={loading} label={sessionForm.id ? "Update Session" : "Add Session"} onCancel={sessionForm.id ? () => setSessionForm(emptySession) : null} />
          </form>

          <SimpleTable
            headers={["Session", "Academic Year", "Term", "Status", "Student Access", "Assessments", "Actions"]}
            rows={sessions.map((session) => [
              session.name,
              session.academic_year_name,
              session.term_name || "-",
              <Status value={session.status} />,
              session.publish_to_students ? (session.restrict_unpaid_students ? "Published with fee check" : "Published") : "Not published",
              session.assessments_count || 0,
              <ActionButtons onEdit={() => editSession(session)} onDelete={() => deleteRecord(`exams/sessions/${session.id}/`, session.name)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "assessments" && (
        <SetupCard title="Assessments">
          <form className="row g-3 mb-4" onSubmit={saveAssessment}>
            <SelectField label="Session" value={assessmentForm.session} onChange={(value) => setAssessmentForm((prev) => ({ ...prev, session: value }))} options={sessions.map((item) => [item.id, item.name])} />
            <SelectField label="Class Group" value={assessmentForm.class_group} onChange={(value) => setAssessmentForm((prev) => ({ ...prev, class_group: value, class_subject: "" }))} options={classGroups.map((item) => [item.id, item.display_name || item.name])} />
            <SelectField label="Subject" value={assessmentForm.class_subject} onChange={(value) => setAssessmentForm((prev) => ({ ...prev, class_subject: value, teacher: "" }))} options={filteredClassSubjects.map((item) => [item.id, `${item.class_group_name} - ${item.subject_name}`])} />
            <SelectField label="Assessment Type" value={assessmentForm.assessment_type} onChange={(value) => setAssessmentForm((prev) => ({ ...prev, assessment_type: value }))} options={assessmentTypes.map((item) => [item.id, item.name])} />
            <SelectField label="Teacher" value={assessmentForm.teacher || ""} onChange={(value) => setAssessmentForm((prev) => ({ ...prev, teacher: value }))} options={filteredAssessmentTeachers.map((item) => [item.id, item.full_name])} required={false} />
            {assessmentForm.class_subject && filteredAssessmentTeachers.length === 0 && (
              <div className="col-12">
                <div className="alert alert-warning py-2 mb-0">
                  No allocated teacher was found for this class subject. Add a Teacher Allocation first.
                </div>
              </div>
            )}
            <TextField label="Maximum Score" type="number" value={assessmentForm.max_score} onChange={(value) => setAssessmentForm((prev) => ({ ...prev, max_score: value }))} required />
            <TextField label="Weight" type="number" value={assessmentForm.weight} onChange={(value) => setAssessmentForm((prev) => ({ ...prev, weight: value }))} required />
            <TextField label="Due Date" type="date" value={assessmentForm.due_date || ""} onChange={(value) => setAssessmentForm((prev) => ({ ...prev, due_date: value }))} />
            <SelectField label="Status" value={assessmentForm.status} onChange={(value) => setAssessmentForm((prev) => ({ ...prev, status: value }))} options={[["DRAFT", "Draft"], ["OPEN", "Open for Marks"], ["CLOSED", "Closed"], ["VERIFIED", "Verified"]]} />
            <div className="col-12">
              <input className="form-control" placeholder="Notes" value={assessmentForm.notes || ""} onChange={(event) => setAssessmentForm((prev) => ({ ...prev, notes: event.target.value }))} />
            </div>
            <SaveBar loading={loading} label={assessmentForm.id ? "Update Assessment" : "Add Assessment"} onCancel={assessmentForm.id ? () => setAssessmentForm(emptyAssessment) : null} />
          </form>

          <SimpleTable
            headers={["Session", "Class", "Subject", "Type", "Teacher", "Max", "Status", "Marks", "Actions"]}
            rows={assessments.map((assessment) => [
              assessment.session_name,
              assessment.class_group_name,
              assessment.subject_name,
              assessment.assessment_type_name,
              assessment.teacher_name || "-",
              assessment.max_score,
              <Status value={assessment.status} />,
              assessment.marks_count || 0,
              <ActionButtons onEdit={() => editAssessment(assessment)} onDelete={() => deleteRecord(`exams/assessments/${assessment.id}/`, assessment.subject_name)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "marks" && (
        <SetupCard title="Marks Entry">
          <form onSubmit={saveMarks}>
            <div className="row g-3 mb-3">
              <SelectField label="Assessment" value={selectedAssessment} onChange={loadMarksForAssessment} options={assessments.map((item) => [item.id, `${item.session_name} - ${item.class_group_name} - ${item.subject_name} (${item.assessment_type_name})`])} />
              <div className="col-md-8 d-flex align-items-end">
                {selectedAssessmentRecord && (
                  <div className="alert alert-light border mb-0 w-100">
                    <strong>{selectedAssessmentRecord.subject_name}</strong> for {selectedAssessmentRecord.class_group_name}.
                    Maximum score: {selectedAssessmentRecord.max_score}. Session: {activeSession?.name || selectedAssessmentRecord.session_name}.
                  </div>
                )}
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Student</th>
                    <th style={{ width: 140 }}>Score</th>
                    <th style={{ width: 110 }}>Absent</th>
                    <th>Remark</th>
                    <th style={{ width: 110 }}>Grade</th>
                    <th style={{ width: 120 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {markRows.length ? markRows.map((row) => (
                    <tr key={row.student}>
                      <td>
                        <div className="fw-semibold">{row.student_name}</div>
                        <small className="text-muted">{row.student_number}</small>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control form-control-sm"
                          value={row.raw_score}
                          disabled={row.is_absent}
                          onChange={(event) => updateMarkRow(row.student, "raw_score", event.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={row.is_absent}
                          onChange={(event) => updateMarkRow(row.student, "is_absent", event.target.checked)}
                        />
                      </td>
                      <td>
                        <input className="form-control form-control-sm" value={row.remark || ""} onChange={(event) => updateMarkRow(row.student, "remark", event.target.value)} />
                      </td>
                      <td>{row.grade || "-"}</td>
                      <td><Status value={row.status} /></td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">Choose an assessment to load students.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <SaveBar loading={loading} label="Save Marks" />
          </form>
        </SetupCard>
      )}

      {activeTab === "reports" && (
        <SetupCard title="Report Cards">
          <form className="row g-3 mb-4" onSubmit={openReportCard}>
            <SelectField label="Session" value={reportFilters.session} onChange={(value) => setReportFilters((prev) => ({ ...prev, session: value }))} options={sessions.map((item) => [item.id, item.name])} />
            <SelectField label="Class Group" value={reportFilters.class_group} onChange={loadReportStudents} options={classGroups.map((item) => [item.id, item.display_name || item.name])} />
            <SelectField label="Student" value={reportFilters.student} onChange={(value) => setReportFilters((prev) => ({ ...prev, student: value }))} options={students.map((item) => [item.id, `${item.student_number} - ${item.full_name}`])} />
            <SaveBar loading={loading} label="View Report Card" />
          </form>

          <SimpleTable
            headers={["Student", "Subject", "Assessment", "Score", "Grade", "Status"]}
            rows={marks.slice(0, 60).map((mark) => [
              mark.student_name,
              mark.subject_name,
              mark.assessment_name,
              mark.is_absent ? "Absent" : `${mark.raw_score || 0} (${mark.percentage || 0}%)`,
              mark.grade || "-",
              <Status value={mark.status} />,
            ])}
          />
        </SetupCard>
      )}

      {reportCard && <ReportCardModal data={reportCard} onClose={() => setReportCard(null)} />}
    </div>
  );
}

function selectedAssessmentSession(assessments, selectedAssessment) {
  return assessments.find((item) => String(item.id) === String(selectedAssessment))?.session;
}

function cleanPayload(values) {
  const payload = {};
  Object.entries(values).forEach(([key, value]) => {
    if (key === "id") return;
    if (value !== "" && value !== null && value !== undefined) {
      payload[key] = value;
    }
  });
  return payload;
}

function showError(error, fallback) {
  const data = error.response?.data;
  const message = data?.detail || Object.values(data || {})?.flat?.()?.[0] || fallback;
  toast.error(message);
}

function Summary({ label, value }) {
  return (
    <div className="col-6 col-xl-3">
      <div className="border rounded-3 p-3 bg-light h-100">
        <small className="text-muted fw-bold">{label}</small>
        <div className="fs-5 fw-bold" style={{ color: "#125b2d" }}>{value}</div>
      </div>
    </div>
  );
}

function SetupCard({ title, children }) {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white"><h6 className="fw-bold mb-0">{title}</h6></div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder = "", type = "text", required = false }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <input type={type} className="form-control" placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} required={required} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, required = true }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select className="form-select" value={value || ""} onChange={(event) => onChange(event.target.value)} required={required}>
        <option value="">Select {label.toLowerCase()}</option>
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </div>
  );
}

function SwitchField({ label, checked, onChange }) {
  return (
    <div className="col-md-4 d-flex align-items-end">
      <label className="form-check form-switch border rounded-3 p-3 w-100 mb-0">
        <input type="checkbox" className="form-check-input ms-0 me-2" checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
        <span className="form-check-label fw-semibold">{label}</span>
      </label>
    </div>
  );
}

function SaveBar({ loading, label, onCancel }) {
  return (
    <div className="col-12 d-flex flex-wrap gap-2 mt-3">
      <button className="btn btn-success fw-semibold" disabled={loading}>{loading ? "Saving..." : label}</button>
      {onCancel && <button type="button" className="btn btn-light border fw-semibold" onClick={onCancel}>Cancel Edit</button>}
    </div>
  );
}

function Status({ value }) {
  const normalized = String(value || "").toUpperCase();
  const successValues = ["ACTIVE", "OPEN", "PUBLISHED", "VERIFIED", "SUBMITTED", "PAID"];
  const warningValues = ["DRAFT", "CLOSED", "PARTIALLY_PAID"];
  const className = successValues.includes(normalized)
    ? "bg-success"
    : warningValues.includes(normalized)
      ? "bg-warning text-dark"
      : "bg-secondary";
  return <span className={`badge ${className}`}>{value || "-"}</span>;
}

function ActionButtons({ onEdit, onDelete }) {
  return (
    <div className="d-flex align-items-center gap-1">
      <button type="button" className="btn btn-sm btn-light border" title="Edit" onClick={onEdit}><i className="bi bi-pencil-square text-success"></i></button>
      <button type="button" className="btn btn-sm btn-light border" title="Delete" onClick={onDelete}><i className="bi bi-trash text-danger"></i></button>
    </div>
  );
}

function SimpleTable({ headers, rows }) {
  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light"><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead>
        <tbody>
          {rows.length ? rows.map((row, index) => (
            <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>
          )) : (
            <tr><td colSpan={headers.length} className="text-center text-muted py-4">No records found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ReportCardModal({ data, onClose }) {
  const feeAccess = data.fee_access || {};
  const school = data.school_profile || {};

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div>
              <h5 className="modal-title fw-bold mb-0">Student Report Card</h5>
              <small className="text-muted">{data.session?.name}</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <ReportCardHeader school={school} />

            <div className="row g-3 mb-3">
              <Summary label="Student" value={data.student?.name || "-"} />
              <Summary label="Class" value={data.student?.class_group || "-"} />
              <Summary label="Average" value={`${data.overall_average || 0}%`} />
              <Summary label="Grade" value={data.overall_grade || "-"} />
            </div>

            {feeAccess.has_arrears && (
              <div className={`alert ${feeAccess.can_view_results ? "alert-warning" : "alert-danger"} border`}>
                <strong>Fee access:</strong> {feeAccess.restriction_message || "Student has outstanding fees."}
                <span className="ms-2">Outstanding balance: {feeAccess.outstanding_balance}</span>
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Subject</th>
                    <th>Assessments</th>
                    <th>Average</th>
                    <th>Grade</th>
                    <th>Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.subjects || []).length ? data.subjects.map((subject) => (
                    <tr key={subject.class_subject}>
                      <td>
                        <div className="fw-semibold">{subject.subject_name}</div>
                        <small className="text-muted">{subject.subject_code}</small>
                      </td>
                      <td>
                        {(subject.assessments || []).map((assessment) => (
                          <div key={assessment.assessment} className="small">
                            {assessment.assessment_type}: {assessment.is_absent ? "Absent" : `${assessment.raw_score}/${assessment.max_score}`} ({assessment.percentage}%)
                          </div>
                        ))}
                      </td>
                      <td>{subject.average}%</td>
                      <td>{subject.grade || "-"}</td>
                      <td>{subject.remark || "-"}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5" className="text-center text-muted py-4">No marks recorded for this session.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {data.session?.report_footer && (
              <div className="border rounded-3 p-3 bg-light">
                {data.session.report_footer}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-success" onClick={() => printReportCard(data)}>
              <i className="bi bi-printer me-2"></i>
              Print Report Card
            </button>
            <button type="button" className="btn btn-light border" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReportCardHeader({ school }) {
  return (
    <div className="border-bottom pb-3 mb-3">
      <div className="d-flex align-items-center gap-3">
        {school.logo_url && (
          <img
            src={school.logo_url}
            alt="School logo"
            style={{ width: 78, height: 78, objectFit: "contain" }}
          />
        )}
        <div>
          <h4 className="fw-bold mb-1">{school.name || "The Grange School"}</h4>
          {school.motto && <div className="text-muted fst-italic mb-1">{school.motto}</div>}
          <div className="small text-muted">
            {[school.address, school.city, school.province, school.country].filter(Boolean).join(", ")}
          </div>
          <div className="small text-muted">
            {[school.phone, school.alternative_phone, school.email, school.website].filter(Boolean).join(" | ")}
          </div>
        </div>
      </div>
      {school.report_header && (
        <div className="small text-muted mt-2">{school.report_header}</div>
      )}
    </div>
  );
}

function printReportCard(data) {
  const school = data.school_profile || {};
  const feeAccess = data.fee_access || {};
  const subjects = data.subjects || [];
  const reportWindow = window.open("", "_blank", "width=900,height=1100");

  if (!reportWindow) {
    toast.error("Allow pop-ups to print the report card.");
    return;
  }

  const schoolAddress = [school.address, school.city, school.province, school.country].filter(Boolean).join(", ");
  const schoolContacts = [school.phone, school.alternative_phone, school.email, school.website].filter(Boolean).join(" | ");
  const subjectRows = subjects.length
    ? subjects.map((subject) => `
        <tr>
          <td>
            <strong>${escapeHtml(subject.subject_name)}</strong><br />
            <span>${escapeHtml(subject.subject_code || "")}</span>
          </td>
          <td>
            ${(subject.assessments || []).map((assessment) => `
              <div>
                ${escapeHtml(assessment.assessment_type)}:
                ${assessment.is_absent ? "Absent" : `${escapeHtml(assessment.raw_score)}/${escapeHtml(assessment.max_score)}`}
                (${escapeHtml(assessment.percentage)}%)
              </div>
            `).join("")}
          </td>
          <td>${escapeHtml(subject.average)}%</td>
          <td>${escapeHtml(subject.grade || "-")}</td>
          <td>${escapeHtml(subject.remark || "-")}</td>
        </tr>
      `).join("")
    : `<tr><td colspan="5" class="muted center">No marks recorded for this session.</td></tr>`;

  reportWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>Report Card - ${escapeHtml(data.student?.name || "Student")}</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; color: #111827; margin: 0; padding: 28px; }
          .report { max-width: 900px; margin: 0 auto; }
          .header { display: flex; gap: 18px; align-items: center; border-bottom: 2px solid #125b2d; padding-bottom: 16px; margin-bottom: 18px; }
          .logo { width: 86px; height: 86px; object-fit: contain; }
          h1 { margin: 0; font-size: 24px; color: #125b2d; }
          h2 { margin: 18px 0 10px; font-size: 18px; }
          .muted { color: #6b7280; }
          .motto { font-style: italic; margin-top: 4px; }
          .small { font-size: 12px; line-height: 1.45; }
          .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 16px 0; }
          .box { border: 1px solid #d1d5db; border-radius: 6px; padding: 10px; }
          .label { font-size: 10px; color: #6b7280; text-transform: uppercase; font-weight: 700; margin-bottom: 4px; }
          .value { font-weight: 700; }
          table { width: 100%; border-collapse: collapse; margin-top: 12px; }
          th, td { border: 1px solid #d1d5db; padding: 9px; vertical-align: top; font-size: 13px; }
          th { background: #f3f4f6; text-align: left; }
          .center { text-align: center; }
          .alert { border: 1px solid #f59e0b; background: #fffbeb; padding: 10px; margin: 12px 0; border-radius: 6px; }
          .footer { margin-top: 22px; border-top: 1px solid #d1d5db; padding-top: 12px; font-size: 12px; color: #4b5563; }
          @media print {
            body { padding: 0; }
            .report { max-width: none; }
          }
        </style>
      </head>
      <body>
        <div class="report">
          <div class="header">
            ${school.logo_url ? `<img class="logo" src="${escapeHtml(school.logo_url)}" alt="School logo" />` : ""}
            <div>
              <h1>${escapeHtml(school.name || "The Grange School")}</h1>
              ${school.motto ? `<div class="motto muted">${escapeHtml(school.motto)}</div>` : ""}
              <div class="small muted">${escapeHtml(schoolAddress)}</div>
              <div class="small muted">${escapeHtml(schoolContacts)}</div>
            </div>
          </div>

          ${school.report_header ? `<div class="small muted">${escapeHtml(school.report_header)}</div>` : ""}

          <h2>Student Report Card</h2>
          <div class="grid">
            <div class="box"><div class="label">Student</div><div class="value">${escapeHtml(data.student?.name || "-")}</div></div>
            <div class="box"><div class="label">Student Number</div><div class="value">${escapeHtml(data.student?.student_number || "-")}</div></div>
            <div class="box"><div class="label">Class</div><div class="value">${escapeHtml(data.student?.class_group || "-")}</div></div>
            <div class="box"><div class="label">Session</div><div class="value">${escapeHtml(data.session?.name || "-")}</div></div>
            <div class="box"><div class="label">Average</div><div class="value">${escapeHtml(data.overall_average || 0)}%</div></div>
            <div class="box"><div class="label">Grade</div><div class="value">${escapeHtml(data.overall_grade || "-")}</div></div>
            <div class="box"><div class="label">Remark</div><div class="value">${escapeHtml(data.overall_remark || "-")}</div></div>
            <div class="box"><div class="label">Result Access</div><div class="value">${feeAccess.can_view_results === false ? "Restricted" : "Allowed"}</div></div>
          </div>

          ${feeAccess.has_arrears ? `
            <div class="alert">
              <strong>Fee access:</strong> ${escapeHtml(feeAccess.restriction_message || "Student has outstanding fees.")}
              Outstanding balance: ${escapeHtml(feeAccess.outstanding_balance)}
            </div>
          ` : ""}

          <table>
            <thead>
              <tr>
                <th>Subject</th>
                <th>Assessments</th>
                <th>Average</th>
                <th>Grade</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>${subjectRows}</tbody>
          </table>

          ${data.session?.report_footer ? `<div class="footer">${escapeHtml(data.session.report_footer)}</div>` : ""}
        </div>
        <script>
          window.onload = function () {
            window.focus();
            window.print();
          };
        </script>
      </body>
    </html>
  `);

  reportWindow.document.close();
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export default ExamsResults;
